-- | Durable work-queue state plus domain helpers.
-- |
-- | This module owns routable/domain queue data. Server-derived lookup data lives
-- | in `Steps.WorkQueue.Cache`; view-only widget data lives in
-- | `Steps.WorkQueue.UIState`.
module Steps.WorkQueue.State
    ( FormPlaceholder
    , WorkQueueState
    , FormSectionID
    , WorkQueueNavigation
    , FillQueueEntry
    , initialWorkQueueState
    , resetWorkQueueState
    , formForSelected
    , formForSelected'
    , isWorkQueueEmpty
    , addStudents
    , removeStudents
    , getTeacher
    , getTeacherOrErr
    , getSemester
    , getSemesterOrErr
    , setTeacherAndSemester
    , setCurrentDoc
    , editDocsWorkQueue
    , showFillQueue
    , getFillQueue
    , createAllForms
    ) where

import Common

import API.Account (AccountInfo, AccountType(..))
import API.FormBase (FormID)
import API.Document (CreateFormRequest, FormReport, createAndExport)
import API.FormType (FormPickerMap, emptyFormPickerMap)
import API.RecentForms (RecentForm)
import API.Search (SearchSemester, SearchTeacher)
import API.SelStudent (SelStudent)
import API.Types (StudentID)
import Data.Array (catMaybes, concat)
import Data.Array as Arr
import Data.Either (note)
import Data.Maybe (isJust)
import Error (ErrorWrapper)
import GraphQL.Join (runGraphQL)
import GraphQL.Type (innerId)
import Internal.SelectionMap (SelectionMap, listSelected)
import Internal.SelectionMap as Sel
import Steps.WorkQueue.DocumentIndex (DocumentLookup, DocumentMap, emptyDocMap)

type FormPlaceholder =
    { id :: FormID
    , name :: String
    }

type WorkQueueState =
    { forms :: FormPickerMap
    -- ^ Forms that have been selected to be worked on.

    , students :: SelectionMap StudentID SelStudent
    -- ^ Students that have been selected to be worked on.

    , documents :: DocumentMap
    -- ^ Documents that have been saved to the API.

    , editDocs :: Array RecentForm
    -- ^ Array of existing documents queued for editing.

    , currentDoc :: Maybe DocumentLookup
    -- ^ If we're on step 3, this should point to the document we're currently
    -- working on so the work queue can highlight it.

    , semester :: Maybe SearchSemester
    -- ^ Authoritative semester for document creation and saves. The select
    -- widget in `WorkQueueUiState` mirrors this value for display/input only.

    , teacher :: Maybe SearchTeacher
    -- ^ Authoritative teacher for document creation and saves. The select
    -- widget in `WorkQueueUiState` mirrors this value for display/input only.
    }

-- | Anchor to jump to within a form.
type FormSectionID = String

type WorkQueueNavigation =
    { lookup :: DocumentLookup
    , section :: Maybe FormSectionID
    }

initialWorkQueueState :: WorkQueueState
initialWorkQueueState =
    { forms: emptyFormPickerMap
    , students: Sel.empty
    , documents: emptyDocMap
    , editDocs: []
    , currentDoc: Nothing
    , teacher: Nothing
    , semester: Nothing
    }

resetWorkQueueState
    :: Maybe SearchTeacher
    -> Maybe SearchSemester
    -> WorkQueueState
    -> WorkQueueState
resetWorkQueueState defTeacher defSemester workQ = workQ
    { forms = emptyFormPickerMap
    , students = Sel.empty
    , documents = emptyDocMap
    , editDocs = []
    , currentDoc = Nothing
    , teacher = defTeacher
    , semester = defSemester
    }

-- | Whether the "creating forms for" semester and teacher are selected.
formForSelected :: AccountInfo -> WorkQueueState -> Boolean
formForSelected { type: BoardAccount } { semester } =
    isJust semester
formForSelected _ workQ = formForSelected' workQ

formForSelected' :: WorkQueueState -> Boolean
formForSelected' { teacher, semester } =
    isJust teacher && isJust semester

isWorkQueueEmpty :: WorkQueueState -> Boolean
isWorkQueueEmpty { forms, editDocs, students } =
    (Sel.isEmpty forms || Sel.isEmpty students) && (Arr.length editDocs <= 0)

getTeacher :: WorkQueueState -> Maybe SearchTeacher
getTeacher = _.teacher

getTeacherOrErr :: WorkQueueState -> Either ErrorWrapper SearchTeacher
getTeacherOrErr workQ = note err $ getTeacher workQ
  where
    err = StringError "No teacher selected to fill forms for."

getSemester :: WorkQueueState -> Maybe SearchSemester
getSemester = _.semester

getSemesterOrErr :: WorkQueueState -> Either ErrorWrapper SearchSemester
getSemesterOrErr workQ = note err $ getSemester workQ
  where
    err = StringError "No semester selected to fill forms for."

setTeacherAndSemester :: WorkQueueState -> Maybe SearchTeacher -> SearchSemester -> WorkQueueState
setTeacherAndSemester workQ teacher semester = workQ
    { teacher = teacher
    , semester = Just semester
    }

addStudents :: WorkQueueState -> Array SelStudent -> WorkQueueState
addStudents workQ students =
    workQ { students = Sel.selectAll' students workQ.students true }

removeStudents :: WorkQueueState -> Array SelStudent -> WorkQueueState
removeStudents workQ students =
    workQ { students = Sel.selectAll' students workQ.students false }

setCurrentDoc :: WorkQueueState -> Maybe DocumentLookup -> WorkQueueState
setCurrentDoc workQ doc = workQ { currentDoc = doc }

-- | Sets up the work queue to edit the given list of documents.
editDocsWorkQueue :: WorkQueueState -> Array RecentForm -> WorkQueueState
editDocsWorkQueue workQ docs = workQ
    { editDocs = docs
    , students = Sel.selectAll' (map _.student docs) workQ.students true
    }

-- | An entry in the list of forms to fill produced by the work queue for step 3.
type FillQueueEntry =
    { student :: StudentID
    , form :: Either RecentForm FormID
    }

fillQueueToCreateReq
    :: Maybe SearchTeacher
    -> Maybe SearchSemester
    -> FillQueueEntry
    -> Maybe CreateFormRequest
fillQueueToCreateReq defTeacher defSemester { student, form: Right fid } = Just
    { student_id: student
    , type_id: fid
    , teacher_id: _.id <$> defTeacher
    , semester: _.semester <$> defSemester
    }
fillQueueToCreateReq _ _ { form: Left _ } = Nothing

showFillQueue :: Array FillQueueEntry -> String
showFillQueue fq = show $ map showFQE fq

showFQE :: FillQueueEntry -> String
showFQE { student, form: Right id } = "{ student: " <> innerId student <> ", form: " <> innerId id <> " }"
showFQE { student, form: Left { form_type: { id } } } =
    "{ student: " <> innerId student <> ", form: " <> innerId id <> " }"

getFillQueue :: WorkQueueState -> Array FillQueueEntry
getFillQueue workQ = concat $ map studentForms students
  where
    studentForms :: StudentID -> Array FillQueueEntry
    studentForms student = map (\f -> { student, form: f }) (docs <> map Right forms)
      where
        docs = Left <$> Arr.filter (\f -> f.student.id == student) workQ.editDocs

    students :: Array StudentID
    students = Arr.fromFoldable $ map _.id $ Sel.listSelected workQ.students

    forms :: Array FormID
    forms = Arr.fromFoldable $ map _.id $ listSelected workQ.forms

createAllForms
    :: WorkQueueState
    -> Maybe SearchTeacher
    -> Maybe SearchSemester
    -> Aff (Either ErrorWrapper (Array FormReport))
createAllForms workQ defTeacher defSemester = runGraphQL $
    createAndExport $ catMaybes $ map (fillQueueToCreateReq defTeacher defSemester) $ getFillQueue workQ
